import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Image, Row, Col, Form, Button } from "react-bootstrap";
import { useAlert } from "../../../context/AlertContext";
import { useLoading } from "../../../context/LoadingContext";
import { GooglePinIcon } from "../../../data/iconImage";
import { getServiceRequest } from "../../../commons/ServiceRequest";
import { initializeSetOptionMulti } from "../../../utils/InitializeSetDefault";
import { formatDate, formatDateTableForm } from "../../../commons/FormatValue";
import {
  emptyValueObjects,
  filteredItemByArray
} from "../../../utils/ManageArray";
import {
  FloatingControlComponent,
  SearchSelectComponent
} from "../../FormComponent";
import TableForm from "../../TableForm";
import SearchFilter from "../../SearchFilter";
import ConfirmModal from "../../ConfirmModal";
import CardDisplayComponent from "../../CardDisplayComponent";
import PMNosService from "../../../services/pmno.service";
import EmployeesService from "../../../services/employee.service";

const WorkingPlanForm = ({ editData, onSubmitCallback }) => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { showAlert } = useAlert();
  const { setLoading } = useLoading();
  const [showModal, setShowModal] = useState(false);
  const [validated, setValidated] = useState(false);
  const [PMNos, setPMNos] = useState([]);
  const [Employees, setEmployees] = useState({ admin: [], chief: [] });
  const [data, setData] = useState({ Array: [] });
  const rowData = data.Array?.[0];
  const columns = [
    { name: "Pm_No", label: "หมายเลข PM", readOnly: true },
    { name: "Pm_Item", label: "PM/ครั้งที่", readOnly: true },
    { name: "Month_Item", label: "เดือน/ครั้ง", readOnly: true },
    {
      name: "PMPlan.CustContractItem.CustContract.Project_No",
      label: "เลขที่โครงการ",
      readOnly: true
    },
    {
      name: "PMPlan.CustContractItem.Contract_No",
      label: "เลขที่สัญญา",
      readOnly: true
    },
    {
      name: "PMPlan.CustContractItem.Contract_Type",
      label: "ประเภทที่สัญญา",
      readOnly: true
    },
    {
      name: "PMPlan.CustContractItem.CustContract.Contract_Start_Date",
      label: "วันที่เริ่มสัญญา",
      format: (row) =>
        formatDate(
          row?.PMPlan?.CustContractItem?.CustContract?.Contract_Start_Date
        ),
      readOnly: true
    },
    {
      name: "PMPlan.CustContractItem.CustContract.Contract_End_Date",
      label: "วันที่สิ้นสุดสัญญา",
      format: (row) =>
        formatDate(
          row?.PMPlan?.CustContractItem?.CustContract?.Contract_End_Date
        ),
      readOnly: true
    },
    {
      name: "PMPlan.Customer.Cus_Code",
      label: "รหัสลูกค้า/สาขา",
      readOnly: true
    },
    {
      name: "PMPlan.Customer.Cus_NumBranch",
      label: "เลขที่สาขา",
      readOnly: true
    },
    {
      name: "PMPlan.Customer.Cus_Name",
      label: "ชื่อลูกค้า/สาขา",
      readOnly: true
    },
    {
      name: "Job_Start_Date",
      label: "วันที่เริ่มงาน",
      type: "date",
      format: (row) => formatDateTableForm(row?.Job_Start_Date)
    },
    {
      name: "Job_Finish_Date",
      label: "วันที่เสร็จงาน",
      type: "date",
      format: (row) => formatDateTableForm(row?.Job_Finish_Date)
    }
  ];
  const searchInput = [
    {
      name: "PMPlan.Plan_No",
      label: "หมายเลขแผน PM",
      classNames: { col: "mb-3", label: "mb-1" },
      type: !editData ? "select" : "text",
      disabled: !!editData,
      value: editData?.PMPlan?.Plan_No || null
    },
    {
      name: "Pm_Item",
      label: "PM/ครั้งที่",
      classNames: { col: "mb-3", label: "mb-1" },
      type: !editData ? "select" : "text",
      size: null,
      minKeywordLength: 1,
      disabled: !!editData,
      value: editData?.Pm_Item || null
    },
    {
      name: "PMPlan.CustContractItem.CustContract.Customer.Cus_Code",
      label: "รหัสลูกค้า",
      classNames: { col: "mb-3", label: "mb-1" },
      type: !editData ? "select" : "text",
      disabled: !!editData,
      value:
        editData?.PMPlan?.CustContractItem?.CustContract?.Customer?.Cus_Code ||
        null
    },
    {
      name: "PMPlan.CustContractItem.CustContract.Customer.Cus_Name",
      label: "ชื่อลูกค้า",
      classNames: { col: "mb-3", label: "mb-1" },
      type: !editData ? "select" : "text",
      size: "4_5",
      disabled: !!editData,
      value:
        editData?.PMPlan?.CustContractItem?.CustContract?.Customer?.Cus_Name ||
        null
    },
    {
      name: "PMPlan.Customer.Cus_Code",
      label: "รหัสสาขา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: !editData ? "select" : "text",
      disabled: !!editData,
      value: editData?.PMPlan?.Customer?.Cus_Code || null
    },
    {
      name: "PMPlan.Customer.Cus_NumBranch",
      label: "เลขที่สาขา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: !editData ? "select" : "text",
      size: null,
      disabled: !!editData,
      value: editData?.PMPlan?.Customer?.Cus_NumBranch || null
    },
    {
      name: "PMPlan.Customer.Cus_Name",
      label: "ชื่อสาขา",
      classNames: { col: "mb-3", label: "mb-1" },
      type: !editData ? "select" : "text",
      size: "7_5",
      disabled: !!editData,
      value: editData?.PMPlan?.Customer?.Cus_Name || null
    }
  ];
  const inputField = [
    {
      name: "Admin_Brand",
      label: "ธุรการผู้รับผิดชอบแบรนด์",
      classNames: { col: "mb-3", label: "mb-1" },
      type: !editData ? "select" : "text",
      disabled: !!editData,
      minKeywordLength: 1,
      filterBy: ["Employee_Name", "Employee_Surname"],
      options: initializeSetOptionMulti({
        key: "Id",
        label: ["Employee_Name", "Employee_Surname"],
        operator: " ",
        dataset: Employees?.admin
      }),
      queryService: (query) =>
        getServiceRequest({
          showAlert,
          setLoading,
          serviceRequest: EmployeesService.getAllEmployees(query),
          callback: ({ items }) =>
            setEmployees((prev) => ({
              ...prev,
              admin: items.filter(
                ({ Employee_Name, Employee_Surname }) =>
                  `${Employee_Name} ${Employee_Surname}` !==
                  rowData?.Team_Chief_Mc
              )
            }))
        }),
      value: editData?.Admin_Brand || null
    },
    {
      name: "Team_Chief_Mc",
      label: "ชื่อทีม/หัวหน้าช่าง",
      classNames: { col: "mb-3", label: "mb-1" },
      type: !editData ? "select" : "text",
      size: 9,
      disabled: !!editData,
      minKeywordLength: 1,
      filterBy: ["Employee_Name", "Employee_Surname"],
      options: initializeSetOptionMulti({
        key: "Id",
        label: ["Employee_Name", "Employee_Surname"],
        operator: " ",
        dataset: Employees?.chief
      }),
      queryService: (query) =>
        getServiceRequest({
          showAlert,
          setLoading,
          serviceRequest: EmployeesService.getAllEmployees(query),
          callback: ({ items }) =>
            setEmployees((prev) => ({
              ...prev,
              chief: items.filter(
                ({ Employee_Name, Employee_Surname }) =>
                  `${Employee_Name} ${Employee_Surname}` !==
                  rowData?.Admin_Brand
              )
            }))
        }),
      value: editData?.Team_Chief_Mc || null
    },
    {
      name: "Google_Map_Link",
      label: "Google Map",
      type: "text",
      size: null,
      disabled: !!editData,
      value: editData?.Google_Map_Link || null
    },
    {
      name: "Google_Map_Btn",
      classNames: { col: "ps-1 d-flex align-items-center" },
      type: "button",
      icon: { src: GooglePinIcon, alt: "Google Map Pin Icon" },
      style: { col: { width: "58px" } },
      onClick: () =>
        window.open(
          rowData?.Google_Map_Link || "https://maps.google.com/",
          "_blank"
        )
    }
  ];

  const checkValidity = () => {
    if (data.Array.length === 0) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณาเลือกข้อมูลแผน PM",
        type: "danger"
      });
      return false;
    }
    if (emptyValueObjects(data.Array, ["Employee", "Employee_Id"]).length > 0) {
      showAlert({
        error: "ข้อผิดพลาด",
        message: "กรุณากรอกข้อมูลให้ครบถ้วน",
        type: "danger"
      });
      return false;
    }
    return true;
  };

  const handleValidated = (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (!form.checkValidity() || !checkValidity()) {
      event.stopPropagation();
      setValidated(true);
    } else {
      setShowModal(!showModal);
    }
  };

  const handleSubmit = (event, { dataCallback, showCallback }) => {
    event.preventDefault();
    setShowModal(showCallback);

    if (dataCallback) {
      const modifiedData = {
        Array: dataCallback.Array.map(
          ({
            Id,
            Deleted,
            Employee,
            Employee_Id,
            PMPlan,
            createdAt,
            createdBy,
            updatedAt,
            updatedBy,
            ...item
          }) => (!editData ? { ...item, Id } : item)
        )
      };
      onSubmitCallback(modifiedData);
    }
  };

  useEffect(() => {
    if (!editData) {
      getServiceRequest({
        showAlert,
        setLoading,
        serviceRequest: PMNosService.getAllPMNos(),
        callback: (res) => setPMNos(res)
      });
    } else setData({ Array: [editData] });
  }, [editData]);

  return (
    <>
      <CardDisplayComponent
        body={{
          as: Row,
          element: (
            <>
              <SearchFilter
                data={PMNos}
                inputField={searchInput}
                searchCallback={(search) =>
                  setData({
                    Array: filteredItemByArray(PMNos, search).map((item) => ({
                      ...item,
                      Admin_Brand: rowData?.Admin_Brand || "",
                      Team_Chief_Mc: rowData?.Team_Chief_Mc || "",
                      Google_Map_Link: rowData?.Google_Map_Link || ""
                    }))
                  })
                }
              />
              {inputField.map(
                (
                  {
                    name,
                    label,
                    type,
                    size,
                    icon,
                    classNames,
                    options,
                    filterBy,
                    noneAsync,
                    queryService,
                    minKeywordLength,
                    onClick,
                    disabled,
                    readOnly,
                    style,
                    value
                  },
                  idx
                ) => {
                  const result = (value && value) || rowData?.[name];

                  const handleChange = ({ name, value }) => {
                    const updatedData = data.Array.map((item) => ({
                      ...item,
                      [name]: value
                    }));
                    setData({ Array: updatedData });
                  };

                  return (
                    <Col
                      key={`${name}-${idx}`}
                      sm={size ?? (size === null ? null : 3)}
                      className={classNames?.col}
                      style={style?.col}
                    >
                      {type === "button" ? (
                        <Button
                          variant="outline-light"
                          className="d-flex align-items-center border-dark rounded-pill"
                          onClick={onClick}
                        >
                          <Image src={icon?.src} alt={icon?.alt} />
                        </Button>
                      ) : type === "select" ? (
                        <SearchSelectComponent
                          labelField={label}
                          floatingClass={classNames?.label}
                          placeholder="โปรดเลือก"
                          isInvalid={result === ""}
                          options={options}
                          filterBy={filterBy}
                          noneAsync={noneAsync}
                          queryService={queryService}
                          minKeywordLength={minKeywordLength}
                          onChange={(change) =>
                            handleChange({
                              name,
                              value: ["Admin_Brand", "Team_Chief_Mc"].includes(
                                name
                              )
                                ? change?.label
                                : change?.value
                            })
                          }
                          value={`${result}`}
                        />
                      ) : (
                        <FloatingControlComponent
                          type={type}
                          name={name}
                          label={label}
                          readOnly={readOnly}
                          disabled={disabled}
                          className={classNames}
                          placeholder="โปรดระบุ"
                          onChange={({ target }) =>
                            handleChange({ name, value: target.value })
                          }
                          value={result}
                        />
                      )}
                    </Col>
                  );
                }
              )}
            </>
          )
        }}
      />

      <Form noValidate validated={validated} onSubmit={handleValidated}>
        <CardDisplayComponent
          header={{ title: { text: "รายละเอียดแผน PM" } }}
          body={{
            element: (
              <TableForm
                editForm
                data={data.Array}
                columns={columns}
                onChangeCallback={(newData) => setData({ Array: newData })}
              />
            )
          }}
          footer={{
            className: "text-end",
            element: (
              <>
                <Button
                  type="submit"
                  variant="success"
                  className="me-2 px-5 py-2 text-dark rounded-pill"
                >
                  {editData ? "บันทึกการแก้ไข" : "บันทึกข้อมูล"}
                </Button>
                <ConfirmModal
                  show={showModal}
                  data={data}
                  content={editData ? "edit" : "save"}
                  onSubmitCallback={handleSubmit}
                />

                <Button
                  variant="outline-secondary"
                  className="px-5 py-2 text-dark border-dark border-opacity-50 rounded-pill"
                  onClick={() => navigate("/PM_work/create", { state })}
                >
                  ยกเลิก
                </Button>
              </>
            )
          }}
        />
      </Form>
    </>
  );
};

WorkingPlanForm.propTypes = {
  editData: PropTypes.object,
  onSubmitCallback: PropTypes.func
};

export default WorkingPlanForm;
